import React, { useState } from 'react';
import { X, Building2, User, Mail, Loader2, CheckCircle, AlertCircle, ChevronDown, ChevronUp, FileText } from 'lucide-react';

interface CreateInstitutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  // Institution info
  institutionName: string;
  // Solutions subscribed
  solutions: string[];
  // DG info
  dgLastName: string;
  dgFirstName: string;
  dgEmail: string;
  // Billing info (optional)
  businessName: string;
  ice: string;
  ifiscal: string;
  rc: string;
  patente: string;
  billingEmail: string;
  billingAddress: string;
  billingCity: string;
}

const availableSolutions = [
  { id: 'academic', label: 'Gestion Académique', description: 'Filières, cours, notes' },
  { id: 'finance', label: 'Gestion Financière', description: 'Paiements, factures, bourses' },
  { id: 'marketing', label: 'Gestion Marketing', description: 'Prospects, campagnes' },
];

export const CreateInstitutionModal: React.FC<CreateInstitutionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    institutionName: '',
    solutions: ['academic'],
    dgLastName: '',
    dgFirstName: '',
    dgEmail: '',
    // Billing info (optional)
    businessName: '',
    ice: '',
    ifiscal: '',
    rc: '',
    patente: '',
    billingEmail: '',
    billingAddress: '',
    billingCity: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showBillingSection, setShowBillingSection] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSolutionToggle = (solutionId: string) => {
    setFormData((prev) => ({
      ...prev,
      solutions: prev.solutions.includes(solutionId)
        ? prev.solutions.filter((s) => s !== solutionId)
        : [...prev.solutions, solutionId],
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.institutionName.trim()) {
      setError("Le nom de l'établissement est requis");
      return false;
    }
    if (formData.solutions.length === 0) {
      setError('Veuillez sélectionner au moins une solution');
      return false;
    }
    if (!formData.dgLastName.trim()) {
      setError('Le nom du DG est requis');
      return false;
    }
    if (!formData.dgFirstName.trim()) {
      setError('Le prénom du DG est requis');
      return false;
    }
    if (!formData.dgEmail.trim()) {
      setError("L'email du DG est requis");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.dgEmail)) {
      setError('Veuillez entrer un email valide');
      return false;
    }
    // Validate ICE if provided (must be 15 digits)
    if (formData.ice && !/^\d{15}$/.test(formData.ice)) {
      setError("L'ICE doit contenir exactement 15 chiffres");
      return false;
    }
    // Validate billing email if provided
    if (formData.billingEmail && !emailRegex.test(formData.billingEmail)) {
      setError('Veuillez entrer un email de facturation valide');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== handleSubmit called ===');

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed, starting submission...');
    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('nexedu_access_token');
      console.log('Token:', token ? `exists (${token.substring(0, 20)}...)` : 'MISSING!');
      // Build billing info object (only include non-empty values)
      const billingInfo: Record<string, string> = {};
      if (formData.businessName) billingInfo.businessName = formData.businessName;
      if (formData.ice) billingInfo.ice = formData.ice;
      if (formData.ifiscal) billingInfo.ifiscal = formData.ifiscal;
      if (formData.rc) billingInfo.rc = formData.rc;
      if (formData.patente) billingInfo.patente = formData.patente;
      if (formData.billingEmail) billingInfo.billingEmail = formData.billingEmail;
      if (formData.billingAddress) billingInfo.billingAddress = formData.billingAddress;
      if (formData.billingCity) billingInfo.billingCity = formData.billingCity;

      console.log('Sending data:', {
        name: formData.institutionName,
        solutions: formData.solutions,
        dgFirstName: formData.dgFirstName,
        dgLastName: formData.dgLastName,
        dgEmail: formData.dgEmail,
        ...billingInfo,
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.institutionName,
          solutions: formData.solutions,
          dgFirstName: formData.dgFirstName,
          dgLastName: formData.dgLastName,
          dgEmail: formData.dgEmail,
          ...billingInfo,
        }),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de la création');
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setFormData({
          institutionName: '',
          solutions: ['academic'],
          dgLastName: '',
          dgFirstName: '',
          dgEmail: '',
          businessName: '',
          ice: '',
          ifiscal: '',
          rc: '',
          patente: '',
          billingEmail: '',
          billingAddress: '',
          billingCity: '',
        });
        setShowBillingSection(false);
        onSuccess();
      }, 2000);
    } catch (err) {
      console.error('=== CATCH ERROR ===', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      console.log('=== FINALLY block ===');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        institutionName: '',
        solutions: ['academic'],
        dgLastName: '',
        dgFirstName: '',
        dgEmail: '',
        businessName: '',
        ice: '',
        ifiscal: '',
        rc: '',
        patente: '',
        billingEmail: '',
        billingAddress: '',
        billingCity: '',
      });
      setError(null);
      setSuccess(false);
      setShowBillingSection(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleClose}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Nouvel Établissement
                  </h2>
                  <p className="text-sm text-gray-500">
                    Créer un compte pour une nouvelle institution
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Success State */}
          {success && (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Établissement créé avec succès!
                </h3>
                <p className="text-gray-500">
                  Un email de validation a été envoyé à {formData.dgEmail}
                </p>
              </div>
            </div>
          )}

          {/* Form */}
          {!success && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Error Alert */}
              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle size={20} className="flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Institution Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'Établissement <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="institutionName"
                  value={formData.institutionName}
                  onChange={handleInputChange}
                  placeholder="Ex: HEMS Business School"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Solutions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Solutions Souscrites <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableSolutions.map((solution) => (
                    <label
                      key={solution.id}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.solutions.includes(solution.id)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.solutions.includes(solution.id)}
                        onChange={() => handleSolutionToggle(solution.id)}
                        className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">
                          {solution.label}
                        </p>
                        <p className="text-xs text-gray-500">{solution.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* DG Information Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <User size={20} className="text-gray-400" />
                  <h3 className="font-medium text-gray-900">
                    Informations du Directeur Général
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* DG Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du DG <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="dgLastName"
                      value={formData.dgLastName}
                      onChange={handleInputChange}
                      placeholder="Nom de famille"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* DG First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom du DG <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="dgFirstName"
                      value={formData.dgFirstName}
                      onChange={handleInputChange}
                      placeholder="Prénom"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                {/* DG Email */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email du DG <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail
                      size={20}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="email"
                      name="dgEmail"
                      value={formData.dgEmail}
                      onChange={handleInputChange}
                      placeholder="directeur@etablissement.ma"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Un email de validation sera envoyé à cette adresse pour activer le compte.
                  </p>
                </div>
              </div>

              {/* Billing Information Section (Collapsible) */}
              <div className="border-t border-gray-200 pt-6">
                <button
                  type="button"
                  onClick={() => setShowBillingSection(!showBillingSection)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <FileText size={20} className="text-gray-400" />
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Informations de Facturation
                      </h3>
                      <p className="text-sm text-gray-500">Optionnel - ICE, IF, RC pour les factures</p>
                    </div>
                  </div>
                  {showBillingSection ? (
                    <ChevronUp size={20} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400" />
                  )}
                </button>

                {showBillingSection && (
                  <div className="mt-4 space-y-4">
                    {/* Business Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Raison Sociale
                      </label>
                      <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        placeholder="Ex: HEMS Business School SARL"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* ICE, IF, RC, Patente - Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ICE
                        </label>
                        <input
                          type="text"
                          name="ice"
                          value={formData.ice}
                          onChange={handleInputChange}
                          placeholder="15 chiffres"
                          maxLength={15}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">Identifiant Commun de l'Entreprise</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          IF
                        </label>
                        <input
                          type="text"
                          name="ifiscal"
                          value={formData.ifiscal}
                          onChange={handleInputChange}
                          placeholder="Identifiant Fiscal"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          RC
                        </label>
                        <input
                          type="text"
                          name="rc"
                          value={formData.rc}
                          onChange={handleInputChange}
                          placeholder="Registre de Commerce"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Patente
                        </label>
                        <input
                          type="text"
                          name="patente"
                          value={formData.patente}
                          onChange={handleInputChange}
                          placeholder="Taxe professionnelle"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>

                    {/* Billing Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email de Facturation
                      </label>
                      <input
                        type="email"
                        name="billingEmail"
                        value={formData.billingEmail}
                        onChange={handleInputChange}
                        placeholder="comptabilite@etablissement.ma"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Billing Address & City */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Adresse de Facturation
                        </label>
                        <input
                          type="text"
                          name="billingAddress"
                          value={formData.billingAddress}
                          onChange={handleInputChange}
                          placeholder="123 Boulevard Mohammed V"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ville
                        </label>
                        <input
                          type="text"
                          name="billingCity"
                          value={formData.billingCity}
                          onChange={handleInputChange}
                          placeholder="Casablanca"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <Building2 size={20} />
                      Créer l'établissement
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
